/**
 * Argon2id, the only thing standing between a stolen export file and every
 * hint the user has written down.
 *
 * Parameters are fixed by the design at 64 MiB / t=3 / p=1. They are recorded
 * in the export envelope so a future increase stays backward-compatible: an old
 * file decrypts with its own recorded parameters, and re-wraps with the current
 * ones on the next unlock.
 */

import { argon2id } from 'hash-wasm';
import { fromHex, toHex } from './bytes.js';

export interface KdfParams {
  readonly algorithm: 'argon2id';
  readonly memoryKiB: number;
  readonly iterations: number;
  readonly parallelism: number;
  readonly keyLengthBytes: number;
}

export const CURRENT_KDF: KdfParams = Object.freeze({
  algorithm: 'argon2id',
  memoryKiB: 64 * 1024,
  iterations: 3,
  parallelism: 1,
  keyLengthBytes: 32,
});

export const SALT_BYTES = 16;

/**
 * Rejects anything we would not have written ourselves.
 *
 * An export file is attacker-controlled input. Without this, a hostile file
 * could set memoryKiB to 4 GiB and turn "import a backup" into a denial of
 * service on the user's phone.
 */
export function assertSupportedKdf(params: KdfParams): void {
  if (params.algorithm !== 'argon2id') {
    throw new Error(`unsupported kdf: ${String(params.algorithm)}`);
  }
  const inRange = (n: number, lo: number, hi: number) =>
    Number.isInteger(n) && n >= lo && n <= hi;

  if (!inRange(params.memoryKiB, 16 * 1024, 256 * 1024)) {
    throw new Error('kdf memory out of supported range');
  }
  if (!inRange(params.iterations, 1, 10)) throw new Error('kdf iterations out of range');
  if (!inRange(params.parallelism, 1, 4)) throw new Error('kdf parallelism out of range');
  if (!inRange(params.keyLengthBytes, 32, 64)) throw new Error('kdf key length out of range');
}

/**
 * Derives raw KEK bytes from a normalised passphrase.
 *
 * Returns bytes rather than a CryptoKey so the caller decides whether the
 * result becomes extractable — it never should outside of tests.
 */
export async function deriveKekBytes(
  normalisedPassphrase: string,
  saltHex: string,
  params: KdfParams = CURRENT_KDF,
): Promise<Uint8Array> {
  assertSupportedKdf(params);
  const hex = await argon2id({
    password: normalisedPassphrase,
    salt: fromHex(saltHex),
    memorySize: params.memoryKiB,
    iterations: params.iterations,
    parallelism: params.parallelism,
    hashLength: params.keyLengthBytes,
    outputType: 'hex',
  });
  return fromHex(hex);
}

export function newSaltHex(): string {
  const salt = new Uint8Array(SALT_BYTES);
  crypto.getRandomValues(salt);
  return toHex(salt);
}
